import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import {
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Loader2,
  RefreshCw,
  User,
  Check,
  Wand2,
  Image as ImageIcon,
  Building2,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useSite } from '@/contexts/SiteContext';
import { useAuth } from '@/context/auth-context';
import { toast } from 'react-hot-toast';

interface Sector {
  id: number;
  name: string;
}

interface GeneratedJournalist {
  id: string;
  name: string;
  title: string;
  bio: string;
  style: string;
  photo_url?: string;
  language: string;
  voice_gender: string;
}

interface AIJournalistWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const JOURNALIST_STYLES = [
  {
    value: 'professional',
    label: 'Professional',
    description: 'Structured, authoritative tone',
  },
  {
    value: 'conversational',
    label: 'Conversational',
    description: 'Friendly, approachable style',
  },
  {
    value: 'sporty',
    label: 'Sporty',
    description: 'Dynamic, enthusiastic approach',
  },
  {
    value: 'business',
    label: 'Business',
    description: 'Executive-level strategic insights',
  },
];

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
];

// AI-suggested names based on language and gender
const SUGGESTED_NAMES: Record<string, Record<string, string[]>> = {
  en: {
    male: ['James Mitchell', 'David Chen', 'Marcus Thompson', 'Oliver Hayes'],
    female: ['Sarah Johnson', 'Emily Williams', 'Sophia Martinez', 'Victoria Reed'],
    neutral: ['Alex Morgan', 'Taylor Brooks', 'Jordan Lee', 'Casey Rivera'],
  },
  es: {
    male: ['Carlos García', 'Miguel Fernández', 'Pablo Rodríguez', 'Diego Torres'],
    female: ['María López', 'Carmen Ruiz', 'Elena Martín', 'Isabel Navarro'],
    neutral: ['Alex Moreno', 'Adrián Sánchez', 'Andrea Herrera', 'Dana Ortega'],
  },
  fr: {
    male: ['Pierre Dubois', 'Jean-Luc Martin', 'François Laurent', 'Michel Roux'],
    female: ['Marie Dubois', 'Sophie Bernard', 'Claire Moreau', 'Isabelle Leroy'],
    neutral: ['Dominique Simon', 'Claude Martin', 'Camille Lefebvre', 'Morgan Petit'],
  },
  de: {
    male: ['Hans Müller', 'Thomas Schmidt', 'Michael Weber', 'Andreas Fischer'],
    female: ['Anna Schneider', 'Julia Wagner', 'Lisa Becker', 'Katharina Meyer'],
    neutral: ['Kim Hoffmann', 'Robin Schäfer', 'Nicola Koch', 'Sascha Bauer'],
  },
  it: {
    male: ['Marco Rossi', 'Luca Bianchi', 'Giovanni Ferrari', 'Alessandro Romano'],
    female: ['Maria Rossi', 'Giulia Colombo', 'Francesca Ricci', 'Elena Marino'],
    neutral: ['Andrea Russo', 'Simone Greco', 'Nicola Conti', 'Alex De Luca'],
  },
  pt: {
    male: ['João Silva', 'Pedro Santos', 'Miguel Costa', 'Tiago Ferreira'],
    female: ['Maria Silva', 'Ana Santos', 'Sofia Costa', 'Beatriz Oliveira'],
    neutral: ['Alex Rodrigues', 'Mário Pereira', 'Dana Almeida', 'Jamie Sousa'],
  },
};

export function AIJournalistWizard({
  open,
  onOpenChange,
  onSuccess,
}: AIJournalistWizardProps) {
  const { selectedSite } = useSite();
  const { user } = useAuth();
  const [step, setStep] = useState<'setup' | 'generating' | 'preview'>('setup');
  const [loading, setLoading] = useState(false);
  const [generatingPhoto, setGeneratingPhoto] = useState(false);

  // Form state
  const [language, setLanguage] = useState('en');
  const [style, setStyle] = useState('professional');
  const [voiceGender, setVoiceGender] = useState<'male' | 'female' | 'neutral'>('neutral');
  const [useCustomName, setUseCustomName] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [selectedSectorId, setSelectedSectorId] = useState<string>('');

  // Generated journalist
  const [generatedJournalist, setGeneratedJournalist] = useState<GeneratedJournalist | null>(null);

  // Determine if user is a content manager (creates site-exclusive journalists)
  const isContentManager = user?.role === 'site_content_manager';
  const isSuperAdmin = user?.role === 'super_admin';

  // Fetch sectors on open
  useEffect(() => {
    if (open) {
      fetchSectors();
      // Auto-select site's sector if available
      if (selectedSite?.sector_ids?.[0]) {
        setSelectedSectorId(selectedSite.sector_ids[0].toString());
      } else if (selectedSite?.sector_id) {
        setSelectedSectorId(selectedSite.sector_id.toString());
      }
    }
  }, [open, selectedSite]);

  const fetchSectors = async () => {
    try {
      const response = await authFetch('/api/sectors');
      if (response.ok) {
        const data = await response.json();
        // API returns array directly, not { sectors: [...] }
        setSectors(Array.isArray(data) ? data : (data.sectors || []));
      }
    } catch (err) {
      console.error('Error fetching sectors:', err);
    }
  };

  const handleReset = () => {
    setStep('setup');
    setLanguage('en');
    setStyle('professional');
    setVoiceGender('neutral');
    setUseCustomName(false);
    setCustomName('');
    setCustomTitle('');
    setAdditionalContext('');
    setGeneratedJournalist(null);
  };

  const handleClose = () => {
    handleReset();
    onOpenChange(false);
  };

  const getSuggestedName = () => {
    const names = SUGGESTED_NAMES[language]?.[voiceGender] || SUGGESTED_NAMES.en.neutral;
    return names[Math.floor(Math.random() * names.length)];
  };

  const getSuggestedTitle = (sectorName: string) => {
    const titles = [
      `Senior ${sectorName} Journalist`,
      `${sectorName} Industry Analyst`,
      `Chief ${sectorName} Editor`,
      `${sectorName} Expert Reporter`,
    ];
    return titles[Math.floor(Math.random() * titles.length)];
  };

  const handleGenerate = async () => {
    if (!selectedSectorId) {
      toast.error('Please select a sector');
      return;
    }

    // For content managers, site must be selected
    if (isContentManager && !selectedSite?.id) {
      toast.error('Please select a site first');
      return;
    }

    setStep('generating');
    setLoading(true);

    try {
      const sectorName = sectors.find(s => s.id.toString() === selectedSectorId)?.name || 'General';
      const journalistName = useCustomName && customName.trim() ? customName : getSuggestedName();
      const journalistTitle = useCustomName && customTitle.trim() ? customTitle : getSuggestedTitle(sectorName);

      const response = await authFetch('/api/journalists/generate-with-ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: journalistName,
          title: journalistTitle,
          sector_id: parseInt(selectedSectorId),
          siteId: isContentManager ? selectedSite?.id : null, // Only content managers create site-exclusive
          style,
          language,
          voice_gender: voiceGender,
          additional_context: additionalContext,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedJournalist(data.journalist);

        // Auto-generate photo
        setGeneratingPhoto(true);
        try {
          const photoResponse = await authFetch(`/api/journalists/${data.journalist.id}/generate-photo`, {
            method: 'POST',
          });
          if (photoResponse.ok) {
            const photoData = await photoResponse.json();
            setGeneratedJournalist(prev => prev ? { ...prev, photo_url: photoData.photoUrl } : null);
          }
        } catch (photoError) {
          console.error('Photo generation failed:', photoError);
        } finally {
          setGeneratingPhoto(false);
        }

        setStep('preview');
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate journalist');
      }
    } catch (error) {
      console.error('Error generating journalist:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate journalist');
      setStep('setup');
    } finally {
      setLoading(false);
    }
  };

  const handleRegeneratePhoto = async () => {
    if (!generatedJournalist?.id) return;

    setGeneratingPhoto(true);
    try {
      const response = await authFetch(`/api/journalists/${generatedJournalist.id}/generate-photo`, {
        method: 'POST',
      });
      if (response.ok) {
        const data = await response.json();
        setGeneratedJournalist(prev => prev ? { ...prev, photo_url: data.photoUrl } : null);
        toast.success('Photo regenerated!');
      } else {
        throw new Error('Failed to regenerate photo');
      }
    } catch (error) {
      toast.error('Failed to regenerate photo');
    } finally {
      setGeneratingPhoto(false);
    }
  };

  const handleFinish = () => {
    toast.success('AI Journalist created successfully!');
    onSuccess();
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Quick AI Journalist Creator
          </DialogTitle>
          <DialogDescription>
            {isContentManager && selectedSite ? (
              <span className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                Creating for {selectedSite.name} (organization-exclusive)
              </span>
            ) : (
              'Create a new AI journalist with AI assistance'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {/* Step 1: Quick Setup */}
          {step === 'setup' && (
            <>
              {/* Organization Info for Content Managers */}
              {isContentManager && (
                <Alert>
                  <Building2 className="h-4 w-4" />
                  <AlertDescription>
                    This journalist will be exclusive to your organization ({selectedSite?.name || 'No site selected'}).
                    Only users with access to this site will see them.
                  </AlertDescription>
                </Alert>
              )}

              {/* Sector Selection */}
              <div className="space-y-2">
                <Label htmlFor="sector">Sector Expertise *</Label>
                <Select value={selectedSectorId} onValueChange={setSelectedSectorId}>
                  <SelectTrigger id="sector">
                    <SelectValue placeholder="Select a sector" />
                  </SelectTrigger>
                  <SelectContent>
                    {sectors.map((s) => (
                      <SelectItem key={s.id} value={s.id.toString()}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Quick Options Row */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          <span className="flex items-center gap-2">
                            <span>{lang.flag}</span>
                            <span>{lang.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Style</Label>
                  <Select value={style} onValueChange={setStyle}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {JOURNALIST_STYLES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Voice</Label>
                  <Select value={voiceGender} onValueChange={(v) => setVoiceGender(v as typeof voiceGender)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="neutral">Neutral</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Custom Name Toggle */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-sm">Custom Name & Title</CardTitle>
                      <CardDescription className="text-xs">
                        Let AI suggest a name, or provide your own
                      </CardDescription>
                    </div>
                    <Switch checked={useCustomName} onCheckedChange={setUseCustomName} />
                  </div>
                </CardHeader>
                {useCustomName && (
                  <CardContent className="space-y-3 pt-0">
                    <div>
                      <Label htmlFor="customName" className="text-xs">Name</Label>
                      <Input
                        id="customName"
                        placeholder="e.g., Sarah Johnson"
                        value={customName}
                        onChange={(e) => setCustomName(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="customTitle" className="text-xs">Professional Title</Label>
                      <Input
                        id="customTitle"
                        placeholder="e.g., Senior Tech Journalist"
                        value={customTitle}
                        onChange={(e) => setCustomTitle(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                )}
              </Card>

              {/* Additional Context */}
              <div className="space-y-2">
                <Label htmlFor="context" className="text-sm">
                  Additional Context (Optional)
                </Label>
                <Textarea
                  id="context"
                  placeholder="Any specific expertise, personality traits, or interview focus areas..."
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  rows={2}
                  className="text-sm"
                />
              </div>

              {/* Generate Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={handleGenerate} disabled={!selectedSectorId}>
                  <Wand2 className="h-4 w-4 mr-2" />
                  Generate Journalist
                </Button>
              </div>
            </>
          )}

          {/* Step 2: Generating */}
          {step === 'generating' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-purple-600" />
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold">Creating Your AI Journalist</h3>
                <p className="text-sm text-muted-foreground max-w-md">
                  Generating profile, bio, interview style, and photo...
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && generatedJournalist && (
            <div className="space-y-6">
              <div className="flex gap-6">
                {/* Photo */}
                <div className="flex flex-col items-center space-y-3">
                  <div className="relative w-32 h-32 rounded-full overflow-hidden bg-muted">
                    {generatingPhoto ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-muted">
                        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                      </div>
                    ) : generatedJournalist.photo_url ? (
                      <img
                        src={generatedJournalist.photo_url}
                        alt={generatedJournalist.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <User className="h-12 w-12 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRegeneratePhoto}
                    disabled={generatingPhoto}
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${generatingPhoto ? 'animate-spin' : ''}`} />
                    New Photo
                  </Button>
                </div>

                {/* Details */}
                <div className="flex-1 space-y-3">
                  <div>
                    <h3 className="text-xl font-semibold">{generatedJournalist.name}</h3>
                    <p className="text-sm text-muted-foreground">{generatedJournalist.title}</p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">
                      {LANGUAGES.find(l => l.code === generatedJournalist.language)?.name || generatedJournalist.language}
                    </Badge>
                    <Badge variant="outline">
                      {JOURNALIST_STYLES.find(s => s.value === generatedJournalist.style)?.label || generatedJournalist.style}
                    </Badge>
                    {isContentManager && selectedSite && (
                      <Badge className="bg-purple-100 text-purple-800">
                        <Building2 className="h-3 w-3 mr-1" />
                        {selectedSite.name}
                      </Badge>
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground line-clamp-4">
                    {generatedJournalist.bio}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => setStep('setup')}>
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Start Over
                </Button>
                <Button onClick={handleFinish} className="bg-green-600 hover:bg-green-700">
                  <Check className="h-4 w-4 mr-2" />
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
